import {Card, MatchScorers, Team, TimelineUpdateType} from 'utils/types';
import {TimelineStateData} from 'store/timelineUpdateCmds/types';
import {useCallback, useState} from 'react';

type ScorersFromTimeLineFn = {
  loading: boolean;
  data: TimelineStateData;
  error?: string | boolean;
};

/**
 * Static helper function
 */
const _getScorersFromTimeline = (timelineState: ScorersFromTimeLineFn) => {
  const {data} = timelineState;
  const scorers: MatchScorers = {
    home: [],
    away: [],
  };

  data.updates.forEach(({type, minute, stoppageTime, teamType, content, method}) => {
    if (
      !teamType ||
      teamType === Team.UNDEFINED ||
      ![TimelineUpdateType.SCORE_CHANGE, TimelineUpdateType.RED_CARD].includes(type)
    )
      return;

    switch (type) {
      case TimelineUpdateType.SCORE_CHANGE:
        if (content && typeof content !== 'string' && 'scorer' in content && content.scorer) {
          const entry = `${minute}${stoppageTime !== '' ? ` + ${stoppageTime}` : ''}'${method === 'penalty' ? ' (ΠΕΝ)' : ''}${method === 'own_goal' ? ' (ΑΥΤ)' : ''}`;
          const scorerIndex = scorers[teamType].findIndex(player => player.name === content.scorer);

          if (scorerIndex > -1 && scorers[teamType][scorerIndex]) {
            const previousGoals = scorers[teamType][scorerIndex].goals;
            scorers[teamType][scorerIndex].goals = previousGoals
              ? [entry, ...previousGoals]
              : [entry];
          } else {
            scorers[teamType].push({
              name: content.scorer,
              goals: [entry],
            });
          }
        }
        break;
      case TimelineUpdateType.RED_CARD:
        if (content && typeof content === 'string') {
          const scorerIndex = scorers[teamType].findIndex(player => player.name === content);
          if (scorerIndex > -1 && scorers[teamType][scorerIndex]) {
            scorers[teamType][scorerIndex].card = Card.RED_CARD;
          } else {
            scorers[teamType].push({
              name: content,
              card: Card.RED_CARD,
            });
          }
        }
        break;
      default:
    }
  });

  return scorers;
};

/**
 * Return type of the useMatchScorers hook
 */
type useMatchScorersResult = [
  MatchScorers,
  {getScorersFromTimeline: (timelineState: ScorersFromTimeLineFn) => void}
];

/**
 * Provides list of current match scorers (derived by timeline updates: goal, etc)
 * @param matchId MongoDB match Id (provided by URL)
 */
const useMatchScorers = (): useMatchScorersResult => {
  const [scorers, setScorers] = useState<MatchScorers>({
    home: [],
    away: [],
  });

  const getScorersFromTimeline = useCallback(
    (timelineState: ScorersFromTimeLineFn) => {
      const {loading, error} = timelineState;
      if (!loading && !error) setScorers(_getScorersFromTimeline(timelineState));
    },
    [setScorers]
  );

  return [scorers, {getScorersFromTimeline}];
};

export default useMatchScorers;
